/**
 * Smart Filter System v9.1
 * Zizov Diamonds
 * 
 * Fixes from audit:
 * 1. Focus trap in modal
 * 2. Return focus on close
 * 3. CSS.escape fallback for IE11
 * 4. Event delegation for tags
 * 5. Debounced resize handler
 * 6. Dynamic accordion height
 */

(function() {
  'use strict';

  // ===========================================
  // CONFIGURATION
  // ===========================================
  
  var CONFIG = {
    mobileBreakpoint: 768,
    resizeDebounce: 150,
    priceRanges: [
      { label: 'Under €2,000', min: 0, max: 2000 },
      { label: '€2K - €3,5K', min: 2000, max: 3500 },
      { label: '€3,5K - €5,5K', min: 3500, max: 5500 },
      { label: 'Over €5,500', min: 5500, max: null }
    ]
  };

  // ===========================================
  // STATE
  // ===========================================
  
  var state = {
    isOpen: false,
    priceParamMin: 'filter.v.price.gte',
    priceParamMax: 'filter.v.price.lte',
    selectedPrices: [],
    selectedFilters: [],
    triggerElement: null  // FIX #2: Store trigger for return focus
  };

  // ===========================================
  // CACHED DOM REFERENCES
  // ===========================================
  
  var dom = {
    overlay: null,
    modal: null,
    tagsContainer: null,
    applyBtn: null,
    priceButtons: [],
    checkboxes: [],
    focusableElements: []  // FIX #1: For focus trap
  };

  // ===========================================
  // UTILITIES
  // ===========================================
  
  function isMobile() {
    return window.innerWidth < CONFIG.mobileBreakpoint;
  }

  function getParams() {
    return new URLSearchParams(window.location.search);
  }

  // XSS Protection
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // FIX #3: CSS.escape fallback for IE11
  function cssEscape(str) {
    if (!str) return '';
    if (typeof CSS !== 'undefined' && CSS.escape) {
      return CSS.escape(str);
    }
    // Fallback for IE11
    return str.replace(/([^\w-])/g, '\\$1');
  }

  // Detect price parameter names from theme
  function detectPriceParams() {
    var el = document.querySelector('.price-range');
    if (el && el.dataset) {
      state.priceParamMin = el.dataset.minName || state.priceParamMin;
      state.priceParamMax = el.dataset.maxName || state.priceParamMax;
    }
  }

  // ===========================================
  // URL & STATE PARSING
  // ===========================================
  
  function parseCurrentFilters() {
    var params = getParams();
    state.selectedPrices = [];
    state.selectedFilters = [];

    var urlMin = parseFloat(params.get(state.priceParamMin)) || 0;
    var urlMax = parseFloat(params.get(state.priceParamMax)) || Infinity;
    var hasPrice = params.has(state.priceParamMin) || params.has(state.priceParamMax);

    if (hasPrice) {
      CONFIG.priceRanges.forEach(function(range, index) {
        var rangeMax = range.max === null ? Infinity : range.max;
        if (urlMin <= range.min && urlMax >= rangeMax) {
          state.selectedPrices.push(index);
        }
      });
    }

    params.forEach(function(value, key) {
      if (key !== state.priceParamMin && 
          key !== state.priceParamMax && 
          key !== 'sort_by') {
        state.selectedFilters.push({ name: key, value: value, label: value });
      }
    });
  }

  function buildFilterUrl() {
    var params = new URLSearchParams();

    var sort = getParams().get('sort_by');
    if (sort) params.set('sort_by', sort);

    if (state.selectedPrices.length > 0) {
      var minPrice = Infinity;
      var maxPrice = 0;
      var hasOpenEnd = false;

      state.selectedPrices.forEach(function(index) {
        var range = CONFIG.priceRanges[index];
        if (range.min < minPrice) minPrice = range.min;
        if (range.max === null) {
          hasOpenEnd = true;
        } else if (range.max > maxPrice) {
          maxPrice = range.max;
        }
      });

      params.set(state.priceParamMin, minPrice);
      if (!hasOpenEnd && maxPrice > 0) {
        params.set(state.priceParamMax, maxPrice);
      }
    }

    state.selectedFilters.forEach(function(filter) {
      params.append(filter.name, filter.value);
    });

    var queryString = params.toString();
    return queryString ? location.pathname + '?' + queryString : location.pathname;
  }

  function getTotalFilterCount() {
    return state.selectedPrices.length + state.selectedFilters.length;
  }

  // ===========================================
  // FIX #1: FOCUS TRAP
  // ===========================================
  
  function updateFocusableElements() {
    if (!dom.modal) return;
    dom.focusableElements = Array.from(dom.modal.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
  }

  function handleFocusTrap(e) {
    if (e.key !== 'Tab' || !state.isOpen) return;
    
    var focusable = dom.focusableElements;
    if (focusable.length === 0) return;
    
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    
    if (e.shiftKey) {
      // Shift+Tab
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      // Tab
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // ===========================================
  // MODAL OPEN/CLOSE
  // ===========================================
  
  function openModal() {
    if (state.isOpen || !dom.modal || !dom.overlay) return;
    
    // FIX #2: Store trigger element for return focus
    state.triggerElement = document.activeElement;
    
    state.isOpen = true;
    dom.overlay.classList.add('sf-active');
    dom.modal.classList.add('sf-active');
    dom.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Update focusable elements list
    updateFocusableElements();
    
    // Focus close button
    var closeBtn = dom.modal.querySelector('.sf-close');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!state.isOpen || !dom.modal || !dom.overlay) return;
    
    state.isOpen = false;
    dom.overlay.classList.remove('sf-active');
    dom.modal.classList.remove('sf-active');
    dom.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    // FIX #2: Return focus to trigger element
    if (state.triggerElement && typeof state.triggerElement.focus === 'function') {
      state.triggerElement.focus();
    }
  }

  // ===========================================
  // RENDER FUNCTIONS
  // ===========================================
  
  function getCurrentTags() {
    var tags = [];

    state.selectedPrices.forEach(function(index) {
      tags.push({
        type: 'price',
        index: index,
        label: CONFIG.priceRanges[index].label
      });
    });

    state.selectedFilters.forEach(function(filter, index) {
      tags.push({
        type: 'filter',
        index: index,
        label: filter.label,
        name: filter.name,
        value: filter.value
      });
    });

    return tags;
  }

  function renderTags() {
    if (!dom.tagsContainer) return;

    var tags = getCurrentTags();

    if (tags.length === 0) {
      dom.tagsContainer.innerHTML = '';
      dom.tagsContainer.style.display = 'none';
    } else {
      dom.tagsContainer.style.display = 'flex';
      dom.tagsContainer.innerHTML = tags.map(function(tag, i) {
        return '<span class="sf-tag" data-type="' + tag.type + '" data-index="' + tag.index + 
          '" data-name="' + escapeHtml(tag.name || '') + '" data-value="' + escapeHtml(tag.value || '') + '">' +
          escapeHtml(tag.label) +
          '<button type="button" data-action="remove" aria-label="Remove ' + escapeHtml(tag.label) + '">×</button>' +
          '</span>';
      }).join('');
    }

    updateApplyButton();
    updateFocusableElements();
  }

  // FIX #4: Event delegation for tag removal
  function handleTagClick(e) {
    var btn = e.target.closest('button[data-action="remove"]');
    if (!btn) return;
    
    e.stopPropagation();
    
    var tag = btn.closest('.sf-tag');
    if (!tag) return;
    
    var type = tag.dataset.type;
    var index = parseInt(tag.dataset.index, 10);
    
    if (type === 'price') {
      var idx = state.selectedPrices.indexOf(index);
      if (idx !== -1) state.selectedPrices.splice(idx, 1);
      
      if (dom.priceButtons[index]) {
        dom.priceButtons[index].classList.remove('sf-on');
        dom.priceButtons[index].setAttribute('aria-pressed', 'false');
      }
    } else {
      var name = tag.dataset.name;
      var value = tag.dataset.value;
      
      state.selectedFilters = state.selectedFilters.filter(function(f) {
        return !(f.name === name && f.value === value);
      });
      
      var checkbox = dom.modal.querySelector(
        '.sf-checkbox[name="' + cssEscape(name) + '"][value="' + cssEscape(value) + '"]'
      );
      if (checkbox) {
        checkbox.checked = false;
        var option = checkbox.closest('.sf-option');
        if (option) option.classList.remove('sf-on');
      }
    }

    renderTags();
  }

  function updateApplyButton() {
    if (!dom.applyBtn) return;
    
    var count = getTotalFilterCount();
    if (count > 0) {
      dom.applyBtn.textContent = 'Apply ' + count + ' Filter' + (count > 1 ? 's' : '');
    } else {
      dom.applyBtn.textContent = 'Show All Products';
    }
  }

  // ===========================================
  // FIX #6: DYNAMIC ACCORDION HEIGHT
  // ===========================================
  
  function toggleSection(section, forceOpen) {
    if (!section) return;
    
    var content = section.querySelector('.sf-section-content');
    var header = section.querySelector('.sf-section-head');
    if (!content) return;
    
    var isOpen = section.classList.contains('sf-open');
    var shouldOpen = forceOpen !== undefined ? forceOpen : !isOpen;
    
    // Close all sections first
    dom.modal.querySelectorAll('.sf-section').forEach(function(s) {
      var c = s.querySelector('.sf-section-content');
      var h = s.querySelector('.sf-section-head');
      if (c) {
        c.style.maxHeight = '0px';
      }
      s.classList.remove('sf-open');
      if (h) h.setAttribute('aria-expanded', 'false');
    });
    
    // Open this one if needed
    if (shouldOpen) {
      section.classList.add('sf-open');
      if (header) header.setAttribute('aria-expanded', 'true');
      // Calculate actual height
      content.style.maxHeight = content.scrollHeight + 'px';
    }
  }

  // ===========================================
  // BUILD MODAL
  // ===========================================
  
  function buildModal() {
    dom.overlay = document.createElement('div');
    dom.overlay.className = 'sf-overlay';

    dom.modal = document.createElement('div');
    dom.modal.className = 'sf-modal';
    dom.modal.setAttribute('role', 'dialog');
    dom.modal.setAttribute('aria-modal', 'true');
    dom.modal.setAttribute('aria-labelledby', 'sf-modal-title');
    dom.modal.setAttribute('aria-hidden', 'true');

    var priceButtonsHtml = CONFIG.priceRanges.map(function(range, index) {
      var isSelected = state.selectedPrices.indexOf(index) !== -1;
      return '<button type="button" class="sf-price' + (isSelected ? ' sf-on' : '') + '" ' +
        'data-index="' + index + '" ' +
        'aria-pressed="' + isSelected + '">' +
        escapeHtml(range.label) +
        '</button>';
    }).join('');

    var sectionsHtml = buildFilterSections();

    dom.modal.innerHTML = 
      '<div class="sf-head">' +
        '<span class="sf-title" id="sf-modal-title">FILTERS</span>' +
        '<button type="button" class="sf-close" aria-label="Close filters">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" fill="none"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="sf-tags" role="list" aria-label="Active filters"></div>' +
      '<div class="sf-body">' +
        '<div class="sf-section sf-open">' +
          '<button type="button" class="sf-section-head" aria-expanded="true">' +
            '<span>PRICE</span>' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="6 9 12 15 18 9" stroke="currentColor" stroke-width="2" fill="none"/></svg>' +
          '</button>' +
          '<div class="sf-section-content">' +
            '<div class="sf-price-grid">' + priceButtonsHtml + '</div>' +
            '<p class="sf-hint">Select multiple to combine ranges</p>' +
          '</div>' +
        '</div>' +
        sectionsHtml +
      '</div>' +
      '<div class="sf-footer">' +
        '<button type="button" class="sf-btn-clear">Clear All</button>' +
        '<button type="button" class="sf-btn-apply">Apply Filters</button>' +
      '</div>';

    document.body.appendChild(dom.overlay);
    document.body.appendChild(dom.modal);

    // Cache references
    dom.tagsContainer = dom.modal.querySelector('.sf-tags');
    dom.applyBtn = dom.modal.querySelector('.sf-btn-apply');
    dom.priceButtons = Array.from(dom.modal.querySelectorAll('.sf-price'));
    dom.checkboxes = Array.from(dom.modal.querySelectorAll('.sf-checkbox'));

    // Bind events
    bindModalEvents();

    // Initial render
    renderTags();
    
    // FIX #6: Set initial height for open section
    var openSection = dom.modal.querySelector('.sf-section.sf-open');
    if (openSection) {
      var content = openSection.querySelector('.sf-section-content');
      if (content) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(function() {
          content.style.maxHeight = content.scrollHeight + 'px';
        }, 0);
      }
    }
  }

  function buildFilterSections() {
    var drawer = document.querySelector('#FilterDrawer .drawer__scrollable');
    if (!drawer) return '';

    var html = '';
    var groups = drawer.querySelectorAll('.collection-sidebar__group');

    groups.forEach(function(group) {
      var trigger = group.querySelector('.collapsible-trigger-btn, .collapsible-trigger');
      if (!trigger) return;

      var title = (trigger.textContent || '').trim().toUpperCase();
      
      if (title.toLowerCase().indexOf('price') !== -1) return;

      var content = group.querySelector('.collapsible-content__inner');
      if (!content) return;

      var optionsHtml = '';
      var tags = content.querySelectorAll('li.tag');

      tags.forEach(function(tag) {
        if (tag.classList.contains('hide')) return;

        var input = tag.querySelector('input.tag__input');
        var labelEl = tag.querySelector('.tag__text');
        if (!input || !labelEl) return;

        var name = input.getAttribute('name');
        var value = input.value;
        var label = (labelEl.textContent || '').trim();
        if (!name) return;

        var isSelected = state.selectedFilters.some(function(f) {
          return f.name === name && f.value === value;
        }) || input.checked;

        if (isSelected) {
          state.selectedFilters.forEach(function(f) {
            if (f.name === name && f.value === value) {
              f.label = label;
            }
          });
        }

        var countMatch = (tag.textContent || '').match(/\((\d+)\)/);
        var count = countMatch ? countMatch[1] : '';

        optionsHtml += 
          '<label class="sf-option' + (isSelected ? ' sf-on' : '') + '">' +
            '<input type="checkbox" class="sf-checkbox" ' +
              'name="' + escapeHtml(name) + '" ' +
              'value="' + escapeHtml(value) + '" ' +
              'data-label="' + escapeHtml(label) + '" ' +
              (isSelected ? 'checked' : '') + '>' +
            '<span class="sf-checkmark" aria-hidden="true"></span>' +
            '<span class="sf-label">' + escapeHtml(label) + '</span>' +
            (count ? '<span class="sf-count">(' + count + ')</span>' : '') +
          '</label>';
      });

      if (optionsHtml) {
        html += 
          '<div class="sf-section">' +
            '<button type="button" class="sf-section-head" aria-expanded="false">' +
              '<span>' + escapeHtml(title) + '</span>' +
              '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="6 9 12 15 18 9" stroke="currentColor" stroke-width="2" fill="none"/></svg>' +
            '</button>' +
            '<div class="sf-section-content" style="max-height: 0px;">' +
              '<div class="sf-options">' + optionsHtml + '</div>' +
            '</div>' +
          '</div>';
      }
    });

    return html;
  }

  // ===========================================
  // EVENT BINDING
  // ===========================================
  
  function bindModalEvents() {
    // Close button
    var closeBtn = dom.modal.querySelector('.sf-close');
    if (closeBtn) {
      closeBtn.onclick = closeModal;
    }

    // Clear button
    var clearBtn = dom.modal.querySelector('.sf-btn-clear');
    if (clearBtn) {
      clearBtn.onclick = function() {
        var sort = getParams().get('sort_by');
        location.href = sort ? location.pathname + '?sort_by=' + sort : location.pathname;
      };
    }

    // Apply button
    if (dom.applyBtn) {
      dom.applyBtn.onclick = function() {
        closeModal();
        location.href = buildFilterUrl();
      };
    }

    // FIX #4: Event delegation for tags
    if (dom.tagsContainer) {
      dom.tagsContainer.onclick = handleTagClick;
    }

    // FIX #6: Accordion headers with dynamic height
    var headers = dom.modal.querySelectorAll('.sf-section-head');
    headers.forEach(function(header) {
      header.onclick = function() {
        var section = this.closest('.sf-section');
        toggleSection(section);
      };
    });

    // Price buttons
    dom.priceButtons.forEach(function(btn) {
      btn.onclick = function() {
        var priceIndex = parseInt(this.dataset.index, 10);
        var idx = state.selectedPrices.indexOf(priceIndex);
        
        if (idx !== -1) {
          state.selectedPrices.splice(idx, 1);
          this.classList.remove('sf-on');
          this.setAttribute('aria-pressed', 'false');
        } else {
          state.selectedPrices.push(priceIndex);
          this.classList.add('sf-on');
          this.setAttribute('aria-pressed', 'true');
        }
        
        renderTags();
      };
    });

    // Checkboxes
    dom.checkboxes.forEach(function(checkbox) {
      checkbox.onchange = function() {
        var option = this.closest('.sf-option');
        var name = this.name;
        var value = this.value;
        var label = this.dataset.label || value;

        if (this.checked) {
          if (option) option.classList.add('sf-on');
          var exists = state.selectedFilters.some(function(f) {
            return f.name === name && f.value === value;
          });
          if (!exists) {
            state.selectedFilters.push({ name: name, value: value, label: label });
          }
        } else {
          if (option) option.classList.remove('sf-on');
          state.selectedFilters = state.selectedFilters.filter(function(f) {
            return !(f.name === name && f.value === value);
          });
        }

        renderTags();
      };
    });

    // FIX #1: Focus trap - listen for Tab key
    dom.modal.addEventListener('keydown', handleFocusTrap);
  }

  // ===========================================
  // PRICE CHIPS (Outside modal)
  // ===========================================
  
  function buildChips() {
    var filterRow = document.querySelector('.collection-filter');
    if (!filterRow) return;

    var params = getParams();
    var urlMin = parseFloat(params.get(state.priceParamMin)) || 0;
    var urlMax = parseFloat(params.get(state.priceParamMax)) || Infinity;
    var hasPrice = params.has(state.priceParamMin) || params.has(state.priceParamMax);

    var chipsHtml = CONFIG.priceRanges.map(function(range, index) {
      var rangeMax = range.max === null ? Infinity : range.max;
      var isActive = hasPrice && urlMin <= range.min && urlMax >= rangeMax;
      
      if (isActive) {
        return '<button class="sf-chip sf-on" data-index="' + index + '">' +
          '<span>' + escapeHtml(range.label) + '</span>' +
          '<span class="sf-chip-remove" aria-label="Remove">×</span>' +
          '</button>';
      }
      return '<button class="sf-chip" data-index="' + index + '">' + 
        escapeHtml(range.label) + 
        '</button>';
    }).join('');

    var container = document.createElement('div');
    container.className = 'sf-chips';
    container.setAttribute('role', 'group');
    container.setAttribute('aria-label', 'Quick price filters');
    container.innerHTML = chipsHtml;

    filterRow.parentNode.insertBefore(container, filterRow.nextSibling);

    // Event delegation for chips
    container.onclick = function(e) {
      var chip = e.target.closest('.sf-chip');
      if (!chip) return;
      
      var isRemove = e.target.classList.contains('sf-chip-remove');
      var index = parseInt(chip.dataset.index, 10);
      var range = CONFIG.priceRanges[index];
      var newParams = new URLSearchParams();

      var sort = getParams().get('sort_by');
      if (sort) newParams.set('sort_by', sort);

      state.selectedFilters.forEach(function(f) {
        newParams.append(f.name, f.value);
      });

      if (!isRemove && !chip.classList.contains('sf-on')) {
        newParams.set(state.priceParamMin, range.min);
        if (range.max !== null) {
          newParams.set(state.priceParamMax, range.max);
        }
      }

      var queryString = newParams.toString();
      location.href = queryString ? location.pathname + '?' + queryString : location.pathname;
    };
  }

  // ===========================================
  // INTERCEPT THEME BUTTON
  // ===========================================
  
  function interceptButton() {
    var btn = document.querySelector('.collection-filter__item--drawer .js-drawer-open-collection-filters');
    if (!btn) return;

    btn.addEventListener('click', function(e) {
      if (isMobile()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openModal();
      }
    }, true);
  }

  // ===========================================
  // FIX #5: DEBOUNCED RESIZE HANDLER
  // ===========================================
  
  var resizeTimer = null;
  
  function handleResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      if (!isMobile() && state.isOpen) {
        closeModal();
      }
    }, CONFIG.resizeDebounce);
  }

  // ===========================================
  // INITIALIZATION
  // ===========================================
  
  function init() {
    // Only run on collection pages with filter
    if (!document.querySelector('.collection-filter')) return;

    detectPriceParams();
    parseCurrentFilters();
    buildModal();
    buildChips();
    interceptButton();

    window.addEventListener('resize', handleResize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
